import React, { useState, useEffect, useMemo } from 'react';
import { StyleSheet, Text, View, TouchableOpacity, TextInput, ScrollView, Alert, Share, Clipboard, Linking, Switch, Modal, Animated } from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { RootStackParamList } from '../../App';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Round, Definition, Player, GameState } from '../types';
import { useGame } from '../hooks/useGame';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { gameService } from '../services/gameService';
import debounce from 'lodash/debounce';
import { Ionicons } from '@expo/vector-icons';
import WordBankSelector from '../components/WordBankSelector';

type Props = NativeStackScreenProps<RootStackParamList, 'Game'>;

export default function GameScreen({ navigation, route }: Props) {
  const { gameId: routeGameId, playerId: routePlayerId } = route.params || {};
  const [currentGameId, setCurrentGameId] = useState<string | null>(routeGameId || null);
  const [currentPlayerId, setCurrentPlayerId] = useState<string | null>(routePlayerId || null);
  const { game, error, loading, startNewRound, submitDefinition, submitVote } = useGame(currentGameId);
  const [definition, setDefinition] = useState('');
  const [gamePhase, setGamePhase] = useState<'lobby' | 'writing' | 'voting' | 'results' | 'reviewing'>('lobby');
  const [selectedDefinitionId, setSelectedDefinitionId] = useState<string | null>(null);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [isWordBankSelectorVisible, setIsWordBankSelectorVisible] = useState(false);
  const [reviewedDefinitions, setReviewedDefinitions] = useState<Set<string>>(new Set());
  const [hasSubmittedDefinition, setHasSubmittedDefinition] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerAnimation] = useState(new Animated.Value(0));
  const [voteAnimation] = useState(new Animated.Value(0));
  const [isSubmittingDefinition, setIsSubmittingDefinition] = useState(false);
  const [isSubmittingVote, setIsSubmittingVote] = useState(false);

  // Check if the current player is the reader
  const isReader = useMemo(() => {
    if (!game || !currentPlayerId) return false;
    const currentPlayer = game.state.players.find(p => p.id === currentPlayerId);
    return currentPlayer?.isReader || false;
  }, [game, currentPlayerId]);

  // Debounced function to update typing status
  const updateTypingStatus = useMemo(
    () =>
      debounce(async (status: 'typing' | 'idle') => {
        if (currentGameId) {
          await gameService.updateReaderStatus(currentGameId, status);
        }
      }, 500),
    [currentGameId]
  );

  useEffect(() => {
    // Only load from AsyncStorage if we don't have params
    if (!routeGameId || !routePlayerId) {
      const loadGameData = async () => {
        try {
          const [savedGameId, savedPlayerId] = await Promise.all([
            AsyncStorage.getItem('currentGameId'),
            AsyncStorage.getItem('currentPlayerId')
          ]);
          console.log('Loaded from storage:', { savedGameId, savedPlayerId });
          
          if (savedGameId && savedPlayerId) {
            setCurrentGameId(savedGameId);
            setCurrentPlayerId(savedPlayerId);
            
            // Verify game exists and is active
            const isValid = await gameService.verifyGame(savedGameId);
            if (!isValid) {
              console.error('Game not found or no longer active');
              Alert.alert('Error', 'Game not found or no longer active');
              navigation.replace('Home');
              return;
            }
          } else {
            console.error('No game data found in storage');
            Alert.alert('Error', 'Game data not found');
            navigation.replace('Home');
          }
        } catch (err) {
          console.error('Error loading game data:', err);
          Alert.alert('Error', 'Failed to load game data');
          navigation.replace('Home');
        }
      };
      
      loadGameData();
    }
  }, [routeGameId, routePlayerId]);

  // Log game state changes
  useEffect(() => {
    if (game) {
      console.log('Game state updated:', {
        gameId: game.id,
        phase: game.state.phase,
        players: game.state.players.length,
        isReader
      });
    }
  }, [game]);

  // Sync local game phase with server state
  useEffect(() => {
    if (game?.state.phase) {
      setGamePhase(game.state.phase);
    }
  }, [game?.state.phase]);

  // Update server game phase when local phase changes
  const handlePhaseChange = async (newPhase: 'writing' | 'voting' | 'results' | 'reviewing') => {
    if (currentGameId) {
      try {
        await gameService.updateGamePhase(currentGameId, newPhase);
        setGamePhase(newPhase);
      } catch (error) {
        console.error('Error updating game phase:', error);
      }
    }
  };

  // Handle reader typing
  const handleReaderTyping = () => {
    if (isReader) {
      updateTypingStatus('typing');
    }
  };

  // Handle reader stop typing
  const handleReaderStopTyping = () => {
    if (isReader) {
      updateTypingStatus('idle');
    }
  };

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      updateTypingStatus.cancel();
    };
  }, [updateTypingStatus]);

  const handleShare = async () => {
    if (!currentGameId) {
      Alert.alert('Error', 'No game code available to share');
      return;
    }

    try {
      const message = `Join my Speaky Spokey game!\n\nGame Code: ${currentGameId}\n\nHow to join:\n1. Open Speaky Spokey app\n2. Click "Join Game"\n3. Enter the game code above\n\nLet's play! 🎮`;
      await Share.share({
        message,
      });
    } catch (error) {
      Alert.alert('Error', 'Failed to share game code');
    }
  };

  const handleStartRound = async () => {
    try {
      await startNewRound();
      console.log('Round started successfully');
    } catch (error) {
      console.error('Error starting round:', error);
      Alert.alert('Error', 'Failed to start the round');
    }
  };

  const handleSubmitDefinition = async () => {
    if (!definition.trim() || isSubmittingDefinition) {
      return;
    }

    try {
      setIsSubmittingDefinition(true);
      const newDefinition: Definition = {
        text: definition.trim(),
        authorId: currentPlayerId as string,
        votes: [],
        isCorrect: false,
        reviewed: false,
      };
      await submitDefinition(newDefinition);
      setDefinition('');
      setHasSubmittedDefinition(true);
      Alert.alert('Success', 'Your definition has been submitted!');
    } catch (error) {
      console.error('Error submitting definition:', error);
      Alert.alert('Error', 'Failed to submit your definition');
    } finally {
      setIsSubmittingDefinition(false);
    }
  };

  const handleVote = async (definitionId: string) => {
    if (isSubmittingVote) return;

    try {
      setIsSubmittingVote(true);
      // Trigger wiggle animation
      Animated.sequence([
        Animated.timing(voteAnimation, {
          toValue: 1,
          duration: 100,
          useNativeDriver: true
        }),
        Animated.timing(voteAnimation, {
          toValue: -1,
          duration: 100,
          useNativeDriver: true
        }),
        Animated.timing(voteAnimation, {
          toValue: 0,
          duration: 100,
          useNativeDriver: true
        })
      ]).start();

      // For the correct definition, we need to add it to the game's definitions if it's not there
      if (definitionId === 'correct' && game?.currentRound) {
        const correctDefinitionExists = game.currentRound.definitions.some(
          def => def.authorId === 'correct'
        );
        
        if (!correctDefinitionExists) {
          // Add the correct definition to the game's definitions
          const correctDef: Definition = {
            text: game.currentRound.correctDefinition,
            authorId: 'correct',
            votes: [],
            isCorrect: true,
            reviewed: true
          };
          await gameService.submitDefinition(currentGameId as string, correctDef);
        }
      }

      await submitVote(definitionId, currentPlayerId as string);
      setSelectedDefinitionId(definitionId);
    } catch (error) {
      console.error('Error submitting vote:', error);
      Alert.alert('Error', 'Failed to submit your vote');
    } finally {
      setIsSubmittingVote(false);
    }
  };

  const handleCopyCode = () => {
    if (currentGameId) {
      Clipboard.setString(currentGameId);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000); // Reset after 2 seconds
    }
  };

  const handleOpenDisplay = () => {
    if (currentGameId) {
      navigation.navigate('Display', { gameId: currentGameId });
    }
  };

  const handleOpenSettings = () => {
    if (isReader) {
      setIsWordBankSelectorVisible(true);
    }
  };

  // Add handler for word bank selection
  const handleWordBankSelect = async (settings: {
    wordBankId: string;
    difficulty?: 'easy' | 'medium' | 'hard';
    category?: string;
  }) => {
    if (!currentGameId) return;

    try {
      await gameService.updateGameSettings(currentGameId, settings);
      setIsWordBankSelectorVisible(false);
      Alert.alert('Success', 'Word bank settings updated!');
    } catch (err) {
      console.error('Error updating word bank settings:', err);
      Alert.alert('Error', 'Failed to update word bank settings');
    }
  };

  const handleMarkDefinitionCorrect = async (authorId: string) => {
    const currentGame = game; // Create local reference after null check
    if (!currentGame?.currentRound?.definitions) return;
    try {
      const updatedDefinitions = currentGame.currentRound.definitions.map(def => {
        if (def.authorId === authorId) {
          return { ...def, reviewed: true, isCorrect: true };
        }
        return def;
      });
      await gameService.updateDefinitions(currentGameId as string, updatedDefinitions, true);
      setReviewedDefinitions(prev => new Set([...prev, authorId]));
    } catch (error) {
      console.error('Error marking definition as correct:', error);
      Alert.alert('Error', 'Failed to mark definition as correct');
    }
  };

  const handleMarkDefinitionIncorrect = async (authorId: string) => {
    const currentGame = game; // Create local reference after null check
    if (!currentGame?.currentRound?.definitions) return;
    try {
      const updatedDefinitions = currentGame.currentRound.definitions.map(def => {
        if (def.authorId === authorId) {
          return { ...def, reviewed: true, isCorrect: false };
        }
        return def;
      });
      await gameService.updateDefinitions(currentGameId as string, updatedDefinitions, true);
      setReviewedDefinitions(prev => new Set([...prev, authorId]));
    } catch (error) {
      console.error('Error marking definition as incorrect:', error);
      Alert.alert('Error', 'Failed to mark definition as incorrect');
    }
  };

  const handleStartGame = async () => {
    try {
      if (!currentGameId) return;
      await gameService.startGame(currentGameId);
      console.log('Game started successfully');
    } catch (error) {
      console.error('Error starting game:', error);
      Alert.alert('Error', 'Failed to start the game');
    }
  };

  const toggleDrawer = () => {
    const toValue = isDrawerOpen ? 0 : 1;
    Animated.timing(drawerAnimation, {
      toValue,
      duration: 300,
      useNativeDriver: true,
    }).start();
    setIsDrawerOpen(!isDrawerOpen);
  };

  const translateX = drawerAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: [300, 0],
  });

  const renderGameHeader = () => {
    return (
      <View style={styles.headerContainer}>
        <TouchableOpacity 
          style={styles.menuButton}
          onPress={toggleDrawer}
        >
          <Text style={styles.menuButtonText}>☰</Text>
        </TouchableOpacity>
      </View>
    );
  };

  const renderDrawer = () => {
    return (
      <Animated.View style={[
        styles.drawer,
        { transform: [{ translateX }] }
      ]}>
        <TouchableOpacity 
          style={styles.closeDrawer}
          onPress={toggleDrawer}
        >
          <Text style={styles.closeDrawerText}>×</Text>
        </TouchableOpacity>

        <View style={styles.drawerContent}>
          <View style={styles.playersSection}>
            <Text style={styles.sectionTitle}>Connected Players</Text>
            <Text style={styles.playerCount}>
              {game?.state.players.filter(p => !p.isReader).length} players joined
            </Text>
            {game?.state.players.map((player, index) => (
              <View key={player.id} style={[
                styles.playerItem,
                player.isReader && styles.readerCard
              ]}>
                <Text style={[
                  styles.playerName,
                  player.isReader && styles.readerName
                ]}>
                  {player.name}
                </Text>
                {player.isReader && (
                  <View style={styles.readerBadge}>
                    <Text style={styles.readerBadgeText}>READER</Text>
                  </View>
                )}
                <Text style={styles.playerScore}>Score: {player.score}</Text>
              </View>
            ))}
          </View>

          <View style={styles.gameCodeSection}>
            <Text style={styles.gameCodeLabel}>GAME CODE</Text>
            <TouchableOpacity 
              onPress={handleCopyCode}
              style={[
                styles.gameCodeBox,
                copyFeedback && styles.gameCodeBoxCopied
              ]}
              activeOpacity={0.7}
            >
              <Text style={styles.gameCode}>
                {currentGameId || 'Loading...'}
              </Text>
              <Text style={[
                styles.copyHint,
                copyFeedback && styles.copyHintSuccess
              ]}>
                {copyFeedback ? 'Copied!' : 'Tap to copy'}
              </Text>
            </TouchableOpacity>
          </View>

          {isReader && (
            <TouchableOpacity 
              style={styles.wordBankButton}
              onPress={handleOpenSettings}
            >
              <Text style={styles.wordBankText}>
                {game?.state.wordBankId === 'jamaican' ? 'Jamaican Patois' :
                 game?.state.wordBankId === 'tech' ? 'Tech Jargon' :
                 game?.state.wordBankId === 'bajan' ? 'Bajan Dialect' : 'Select Word Bank'}
              </Text>
            </TouchableOpacity>
          )}

          <TouchableOpacity 
            style={styles.displayButton}
            onPress={handleOpenDisplay}
          >
            <Text style={styles.displayButtonText}>Open Display View</Text>
          </TouchableOpacity>
        </View>
      </Animated.View>
    );
  };

  const renderReaderInterface = () => {
    if (!game) return null;

    return (
      <View style={styles.container}>
        {game.state.phase === 'lobby' ? (
          <View style={styles.startContainer}>
            <View style={styles.gameCodeSection}>
              <Text style={styles.gameCodeLabel}>GAME CODE</Text>
              <TouchableOpacity 
                onPress={handleCopyCode}
                style={[
                  styles.gameCodeBox,
                  copyFeedback && styles.gameCodeBoxCopied
                ]}
                activeOpacity={0.7}
              >
                <Text style={styles.gameCode}>
                  {currentGameId || 'Loading...'}
                </Text>
                <Text style={[
                  styles.copyHint,
                  copyFeedback && styles.copyHintSuccess
                ]}>
                  {copyFeedback ? 'Copied!' : 'Tap to copy'}
                </Text>
              </TouchableOpacity>
            </View>

            <TouchableOpacity
              style={styles.wordBankButton}
              onPress={handleOpenSettings}
            >
              <Text style={styles.wordBankText}>
                {game.state.wordBankId === 'jamaican' ? 'Jamaican Patois' :
                 game.state.wordBankId === 'tech' ? 'Tech Jargon' :
                 game.state.wordBankId === 'bajan' ? 'Bajan Dialect' : 'Select Word Bank'}
              </Text>
            </TouchableOpacity>

            <View style={styles.playersSection}>
              <Text style={styles.sectionTitle}>Connected Players</Text>
              <Text style={styles.playerCount}>
                {game.state.players.filter(p => !p.isReader).length} players joined
              </Text>
              {game.state.players.filter(p => !p.isReader).map((player, index) => (
                <View key={player.id} style={styles.playerItem}>
                  <Text style={styles.playerName}>{player.name}</Text>
                </View>
              ))}
            </View>

            <TouchableOpacity
              style={[
                styles.startButton,
                game.state.players.length < 3 && styles.disabledButton
              ]}
              onPress={() => {
                if (game.state.players.length < 3) {
                  toggleDrawer();
                } else {
                  handleStartGame();
                }
              }}
              disabled={false}
            >
              <Text style={styles.buttonText}>
                {game.state.players.length < 3 
                  ? `Waiting for Players (${game.state.players.filter(p => !p.isReader).length}/7)`
                  : 'Start Game'}
              </Text>
            </TouchableOpacity>
            {game.state.players.filter(p => !p.isReader).length >= 2 && (
              <Text style={[styles.waitingHint, styles.prominentHint]}>
                ⏳ No rush! You can wait for more players to join (up to 7 players + 1 reader) before starting
              </Text>
            )}
          </View>
        ) : (
          <>
            <View style={styles.wordContainer}>
              <Text style={styles.wordLabel}>CURRENT WORD</Text>
              <Text style={styles.word}>{game.currentRound.word}</Text>
              <Text style={styles.correctDefinition}>
                {game.currentRound.correctDefinition}
              </Text>
            </View>

            {game.currentRound.phase === 'writing' && (
              <View style={styles.definitionReviewContainer}>
                <Text style={styles.label}>Review submitted definitions:</Text>
                
                {game.currentRound.definitions && game.currentRound.definitions.length > 0 ? (
                  <>
                    {game.currentRound.definitions.map((def, index) => {
                      const author = game.state.players.find(p => p.id === def.authorId);
                      return (
                        <View key={index} style={[
                          styles.definitionReviewCard,
                          def.isCorrect && styles.correctDefinitionCard
                        ]}>
                          <Text style={styles.definitionNumber}>Definition {index + 1}</Text>
                          <Text style={styles.definitionText}>{def.text}</Text>
                          <Text style={styles.authorName}>By: {author?.name || 'Unknown'}</Text>
                          {def.isCorrect ? (
                            <View style={styles.correctBadge}>
                              <Text style={styles.correctBadgeText}>✓ Marked as Correct</Text>
                              <Text style={styles.correctNote}>This definition will not appear in the voting phase</Text>
                            </View>
                          ) : (
                            <View style={styles.reviewButtonsContainer}>
                              <TouchableOpacity
                                style={styles.checkboxContainer}
                                onPress={() => handleMarkDefinitionCorrect(def.authorId)}
                              >
                                <View style={[
                                  styles.checkbox,
                                  def.isCorrect && styles.checkboxChecked
                                ]}>
                                  {def.isCorrect && <Text style={styles.checkmark}>✓</Text>}
                                </View>
                                <Text style={styles.checkboxLabel}>Mark as Correct Definition</Text>
                              </TouchableOpacity>
                            </View>
                          )}
                        </View>
                      );
                    })}

                    {game.currentRound.definitions.length > 0 && (
                      <TouchableOpacity
                        style={[styles.startButton, styles.moveToVotingButton]}
                        onPress={() => handlePhaseChange('voting')}
                      >
                        <Text style={styles.buttonText}>Start Voting Phase</Text>
                      </TouchableOpacity>
                    )}
                  </>
                ) : (
                  <Text style={styles.waitingText}>
                    Waiting for players to submit definitions...
                  </Text>
                )}
              </View>
            )}

            {game.currentRound.phase === 'voting' && renderVotingPhase()}

            {game.currentRound.phase === 'results' && (
              <ScrollView style={styles.resultsContainer}>
                <Text style={styles.resultsTitle}>Round Results</Text>
                {game.currentRound.definitions.map((def, index) => (
                  <View key={index} style={[
                    styles.definitionCard,
                    def.isCorrect && styles.correctDefinition
                  ]}>
                    <Text style={styles.definitionText}>{def.text}</Text>
                    <Text style={styles.authorText}>
                      by {game.state.players.find(p => p.id === def.authorId)?.name}
                    </Text>
                    <Text style={styles.votesText}>
                      Votes: {def.votes?.length || 0}
                    </Text>
                  </View>
                ))}

                <View style={styles.scoreboardContainer}>
                  <Text style={styles.scoreboardTitle}>🏆 Current Standings</Text>
                  {[...game.state.players]
                    .sort((a, b) => (b.score || 0) - (a.score || 0))
                    .map((player, index) => (
                      <View key={player.id} style={[
                        styles.scoreboardRow,
                        index === 0 && styles.firstPlace,
                        index === 1 && styles.secondPlace,
                        index === 2 && styles.thirdPlace,
                      ]}>
                        <Text style={styles.playerRank}>
                          {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `${index + 1}.`}
                        </Text>
                        <Text style={[
                          styles.scoreboardPlayerName,
                          player.isReader && styles.readerName
                        ]}>
                          {player.name} {player.isReader ? '(Reader)' : ''}
                        </Text>
                        <Text style={styles.scoreboardScore}>{player.score || 0} pts</Text>
                      </View>
                  ))}
                </View>

                <TouchableOpacity
                  style={[styles.startButton, styles.nextRoundButton]}
                  onPress={handleStartRound}
                >
                  <Text style={styles.buttonText}>Start Next Round</Text>
                </TouchableOpacity>
              </ScrollView>
            )}
          </>
        )}
      </View>
    );
  };

  const renderVotingPhase = () => {
    if (!game?.currentRound) return null;

    // If the current player is a reader, they shouldn't see voting options
    if (isReader) {
      return (
        <View style={styles.votingContainer}>
          <Text style={styles.votingTitle}>Players are voting on definitions...</Text>
          <Text style={styles.waitingStateCount}>
            {game.currentRound.definitions.reduce((count, def) => count + (def.votes?.length || 0), 0)} 
            {' of '} 
            {game.state.players.filter(p => !p.isReader).length} players have voted
          </Text>
        </View>
      );
    }

    const hasPlayerVoted = game.currentRound.definitions.some(def => 
      def.votes?.includes(currentPlayerId as string)
    );

    // Get all definitions and ensure they have votes array
    const votableDefinitions = game.currentRound.definitions
      .filter(def => {
        // Remove player-submitted definitions that are marked as correct
        if (def.isCorrect && def.authorId !== 'correct') {
          return false;
        }
        return true;
      })
      .map(def => ({
        ...def,
        votes: def.votes || []
      }));

    // Add the correct definition from the game if it's not already included
    const hasCorrectDefinition = votableDefinitions.some(def => def.authorId === 'correct');
    if (!hasCorrectDefinition) {
      votableDefinitions.push({
        text: game.currentRound.correctDefinition,
        authorId: 'correct',
        votes: [],
        isCorrect: true,
        reviewed: true
      });
    }

    // Randomize the order
    const shuffledDefinitions = [...votableDefinitions].sort(() => Math.random() - 0.5);

    if (hasPlayerVoted) {
      // Show only the selected definition after voting
      const selectedDef = game.currentRound.definitions.find(def => 
        def.votes?.includes(currentPlayerId as string)
      );

      return (
        <View style={styles.votingContainer}>
          <Text style={styles.votingTitle}>Your Vote Has Been Recorded!</Text>
          <View style={styles.selectedDefinition}>
            <Text style={styles.definitionNumber}>Your Selected Definition</Text>
            <Text style={styles.definitionText}>{selectedDef?.text}</Text>
          </View>
          <View style={styles.waitingStateContainer}>
            <Text style={styles.waitingStateTitle}>Waiting for other players...</Text>
            <Text style={styles.waitingStateCount}>
              {game.currentRound.definitions.reduce((count, def) => count + (def.votes?.length || 0), 0)} 
              {' of '} 
              {game.state.players.filter(p => !p.isReader).length} players have voted
            </Text>
          </View>
        </View>
      );
    }

    return (
      <View style={styles.votingContainer}>
        <Text style={styles.votingTitle}>Vote for the definition you think is correct:</Text>
        <ScrollView style={styles.definitionsList}>
          {shuffledDefinitions.map((def, index) => (
            <TouchableOpacity
              key={index}
              style={[
                styles.definitionCard,
                def.authorId === currentPlayerId && styles.ownDefinition
              ]}
              onPress={() => handleVote(def.authorId)}
              disabled={isSubmittingVote}
            >
              <Text style={styles.definitionNumber}>Definition {index + 1}</Text>
              <Text style={styles.definitionText}>{def.text}</Text>
              {def.authorId === currentPlayerId && (
                <Text style={styles.ownDefinitionLabel}>(Your definition)</Text>
              )}
              {isSubmittingVote && selectedDefinitionId === def.authorId && (
                <Text style={styles.submittingVoteLabel}>Submitting vote...</Text>
              )}
              <Text style={styles.votesText}>
                Votes: {def.votes?.length || 0}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
    );
  };

  const renderPlayerInterface = () => {
    if (game?.state.phase === 'lobby') {
      const playerCount = game.state.players.filter(p => !p.isReader).length;
      return (
        <View style={styles.waitingContainer}>
          <Text style={styles.waitingText}>⌛️ Waiting for game to start...</Text>
          <Text style={styles.submissionStatus}>
            {playerCount} {playerCount === 1 ? 'player' : 'players'} have joined
          </Text>
          {playerCount >= 2 && (
            <Text style={[styles.waitingHint, styles.prominentHint]}>
              ⏳ No rush! You can wait for more players to join (up to 7 players + 1 reader) before starting
            </Text>
          )}
          <Text style={styles.waitingHint}>The word will appear here when the game begins</Text>
        </View>
      );
    }

    if (!game?.currentRound?.word) return null;

    // If the current player is a reader, they shouldn't see the player interface
    if (isReader) return null;

    return (
      <View style={styles.playerContainer}>
        {game.currentRound.phase === 'writing' && (
          <View style={styles.definitionEntryContainer}>
            <View style={styles.mainContent}>
              <View style={styles.categoryBadge}>
                <Text style={styles.categoryText}>
                  {game.currentRound.category || 'General'}
                </Text>
              </View>

              <Text style={styles.wordToDefine}>{game.currentRound.word}</Text>

              {!hasSubmittedDefinition ? (
                <>
                  <TextInput
                    style={styles.definitionInput}
                    placeholder="Enter your definition..."
                    value={definition}
                    onChangeText={setDefinition}
                    multiline
                    numberOfLines={4}
                    maxLength={200}
                    placeholderTextColor="#666"
                    editable={!isSubmittingDefinition}
                  />

                  <TouchableOpacity
                    style={[
                      styles.submitButton,
                      (!definition.trim() || isSubmittingDefinition) && styles.disabledButton
                    ]}
                    onPress={handleSubmitDefinition}
                    disabled={!definition.trim() || isSubmittingDefinition}
                  >
                    <Text style={styles.submitButtonText}>
                      {isSubmittingDefinition ? 'Submitting...' : 'Submit Definition'}
                    </Text>
                  </TouchableOpacity>
                </>
              ) : (
                <View style={styles.submissionSuccessContainer}>
                  <Text style={styles.submissionSuccessIcon}>✓</Text>
                  <Text style={styles.submissionSuccessTitle}>Definition Submitted!</Text>
                  <Text style={styles.submissionSuccessText}>
                    Waiting for other players to submit their definitions...
                  </Text>
                </View>
              )}
            </View>
          </View>
        )}

        {game.currentRound.phase === 'voting' && (
          <View style={styles.votingContainer}>
            <Text style={styles.wordToDefine}>{game.currentRound.word}</Text>
            {renderVotingPhase()}
          </View>
        )}

        {game.currentRound.phase === 'results' && (
          <ScrollView style={styles.resultsContainer}>
            <Text style={styles.resultsTitle}>Round Results</Text>
            {game.currentRound.definitions.map((def, index) => (
              <View key={index} style={[
                styles.definitionCard,
                def.isCorrect && styles.correctDefinition
              ]}>
                <Text style={styles.definitionText}>{def.text}</Text>
                <Text style={styles.authorText}>
                  by {game.state.players.find(p => p.id === def.authorId)?.name}
                </Text>
                <Text style={styles.votesText}>
                  Votes: {def.votes?.length || 0}
                </Text>
              </View>
            ))}

            <View style={styles.scoreboardContainer}>
              <Text style={styles.scoreboardTitle}>🏆 Current Standings</Text>
              {[...game.state.players]
                .sort((a, b) => (b.score || 0) - (a.score || 0))
                .map((player, index) => (
                  <View key={player.id} style={[
                    styles.scoreboardRow,
                    index === 0 && styles.firstPlace,
                    index === 1 && styles.secondPlace,
                    index === 2 && styles.thirdPlace,
                  ]}>
                    <Text style={styles.playerRank}>
                      {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `${index + 1}.`}
                    </Text>
                    <Text style={[
                      styles.scoreboardPlayerName,
                      player.isReader && styles.readerName
                    ]}>
                      {player.name} {player.isReader ? '(Reader)' : ''}
                    </Text>
                    <Text style={styles.scoreboardScore}>{player.score || 0} pts</Text>
                  </View>
              ))}
            </View>
          </ScrollView>
        )}
      </View>
    );
  };

  // Reset hasSubmittedDefinition when round changes
  useEffect(() => {
    if (game?.state.round) {
      setHasSubmittedDefinition(false);
    }
  }, [game?.state.round]);

  // Add this effect after the other useEffects
  useEffect(() => {
    if (!game?.currentRound || !currentGameId || !isReader) return;

    // Only check during voting phase
    if (game.currentRound.phase !== 'voting') return;

    // Count total votes
    const totalVotes = game.currentRound.definitions.reduce(
      (sum, def) => sum + (def.votes?.length || 0),
      0
    );

    // Count non-reader players (who need to vote)
    const nonReaderCount = game.state.players.filter(p => !p.isReader).length;

    // If everyone has voted, move to results phase
    if (totalVotes >= nonReaderCount) {
      handlePhaseChange('results');
    }
  }, [game?.currentRound?.definitions, game?.state.players, currentGameId, isReader]);

  // Loading state
  if (loading) {
    return (
      <View style={styles.container}>
        <Text style={styles.loadingText}>Loading game...</Text>
      </View>
    );
  }

  // Error state
  if (error) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>{error}</Text>
        <TouchableOpacity
          style={styles.button}
          onPress={() => navigation.replace('Home')}
        >
          <Text style={styles.buttonText}>Return to Home</Text>
        </TouchableOpacity>
      </View>
    );
  }

  // Game not found state
  if (!game || !currentGameId || !currentPlayerId) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>Game not found</Text>
        <TouchableOpacity
          style={styles.button}
          onPress={() => navigation.replace('Home')}
        >
          <Text style={styles.buttonText}>Return to Home</Text>
        </TouchableOpacity>
      </View>
    );
  }

  // Now we know game, currentGameId, and currentPlayerId are not null
  const gameInstance = game;
  const gameId = currentGameId;

  return (
    <SafeAreaView style={styles.container}>
      {renderGameHeader()}
      {renderDrawer()}
      <ScrollView style={styles.scrollView}>
        <View style={styles.content}>
          {isReader ? renderReaderInterface() : renderPlayerInterface()}
        </View>
      </ScrollView>

      <Modal
        visible={isWordBankSelectorVisible}
        animationType="slide"
        transparent={true}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <WordBankSelector onSelect={handleWordBankSelect} />
            <TouchableOpacity
              style={styles.closeButton}
              onPress={() => setIsWordBankSelectorVisible(false)}
            >
              <Text style={styles.closeButtonText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFD84D',
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 20,
    gap: 20,
  },
  readerContainer: {
    gap: 20,
  },
  playerContainer: {
    flex: 1,
    backgroundColor: '#FFD84D',
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 10,
  },
  wordContainer: {
    backgroundColor: '#fff',
    padding: 20,
    borderRadius: 12,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  wordLabel: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#444',
    marginBottom: 5,
  },
  word: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#E63946',
    textAlign: 'center',
    marginBottom: 20,
  },
  correctDefinition: {
    fontSize: 18,
    color: '#2e7d32',
    fontWeight: 'bold',
    textAlign: 'center',
    marginVertical: 15,
    padding: 15,
    backgroundColor: '#e8f5e9',
    borderRadius: 8,
  },
  definitionReviewContainer: {
    gap: 15,
    marginTop: 20,
  },
  definitionReviewCard: {
    backgroundColor: '#fff',
    padding: 20,
    borderRadius: 12,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 6,
    elevation: 4,
  },
  definitionNumber: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#444',
    marginBottom: 10,
  },
  definitionText: {
    fontSize: 42,
    color: '#1E4B8F',
    marginVertical: 20,
    textAlign: 'center',
    fontWeight: '600',
    lineHeight: 48,
    letterSpacing: 0.5,
  },
  authorName: {
    fontSize: 14,
    color: '#666',
    marginBottom: 5,
    textAlign: 'center',
  },
  reviewButtonsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 15,
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: '#4caf50',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: {
    backgroundColor: '#4caf50',
  },
  checkmark: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  checkboxLabel: {
    fontSize: 16,
    color: '#333',
    fontWeight: '500',
  },
  votingContainer: {
    flex: 1,
    padding: 20,
    gap: 15,
  },
  definitionCard: {
    backgroundColor: '#fff',
    padding: 20,
    borderRadius: 12,
    marginBottom: 10,
    shadowColor: '#1E4B8F',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  votesCount: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    marginTop: 5,
  },
  startContainer: {
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 15,
  },
  startButton: {
    backgroundColor: '#E63946',
    padding: 15,
    borderRadius: 25,
    alignItems: 'center',
    width: '100%',
  },
  buttonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
  },
  button: {
    backgroundColor: '#E63946',
    padding: 15,
    borderRadius: 25,
    alignItems: 'center',
  },
  wordBankButton: {
    backgroundColor: '#1E4B8F',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    width: '100%',
    marginBottom: 10,
  },
  wordBankText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  tapToChange: {
    color: '#fff',
    fontSize: 14,
    opacity: 0.8,
  },
  waitingText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1E4B8F',
    textAlign: 'center',
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#444',
    marginBottom: 10,
  },
  headerContainer: {
    backgroundColor: '#E63946',
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  menuButton: {
    padding: 15,
  },
  menuButtonText: {
    fontSize: 24,
    color: '#fff',
  },
  drawer: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: 300,
    backgroundColor: '#fff',
    zIndex: 1000,
    shadowColor: '#000',
    shadowOffset: { width: -2, height: 0 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  closeDrawer: {
    position: 'absolute',
    top: 10,
    right: 10,
    padding: 10,
    zIndex: 1,
  },
  closeDrawerText: {
    fontSize: 24,
    color: '#666',
  },
  drawerContent: {
    flex: 1,
    padding: 20,
    paddingTop: 50,
  },
  gameCodeSection: {
    backgroundColor: '#fff',
    padding: 15,
    borderRadius: 12,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  gameCodeLabel: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#1E4B8F',
    marginBottom: 8,
  },
  gameCode: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1E4B8F',
    letterSpacing: 1,
    textAlign: 'center',
  },
  copyHint: {
    fontSize: 12,
    color: '#666',
    marginTop: 4,
    textAlign: 'center',
  },
  copyHintSuccess: {
    color: '#2e7d32',
    fontWeight: 'bold',
  },
  gameCodeBox: {
    backgroundColor: '#F5F5F5',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    width: '100%',
  },
  gameCodeBoxCopied: {
    backgroundColor: '#e8f5e9',
  },
  copyFeedback: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#4caf50',
    padding: 10,
    alignItems: 'center',
    transform: [{ translateY: 0 }],
  },
  copyFeedbackText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: 'bold',
  },
  copyFeedbackHidden: {
    transform: [{ translateY: 50 }],
  },
  displayButton: {
    backgroundColor: '#fff',
    paddingHorizontal: 15,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 2,
    borderColor: '#fff',
    marginTop: 10,
  },
  displayButtonText: {
    color: '#E63946',
    fontSize: 14,
    fontWeight: 'bold',
  },
  disabledButton: {
    backgroundColor: '#ccc',
  },
  voteBadge: {
    position: 'absolute',
    top: -10,
    right: -10,
    backgroundColor: '#f4511e',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 15,
  },
  voteBadgeText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  waitingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    gap: 15,
  },
  joiningContainer: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  playersHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 15,
  },
  playersTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1E4B8F',
  },
  playersCount: {
    fontSize: 18,
    color: '#666',
    marginLeft: 8,
  },
  playersList: {
    gap: 10,
  },
  playerCard: {
    backgroundColor: '#f8f8f8',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  readerCard: {
    backgroundColor: '#fff3e0',
    borderColor: '#E63946',
  },
  playerName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
  },
  readerName: {
    color: '#E63946',
    fontWeight: 'bold',
  },
  readerBadge: {
    backgroundColor: '#E63946',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginHorizontal: 8,
  },
  readerBadgeText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: 'bold',
  },
  playerScore: {
    fontSize: 14,
    color: '#666',
  },
  definitionsContainer: {
    gap: 15,
  },
  wordDisplay: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#E63946',
    textAlign: 'center',
    marginBottom: 20,
  },
  lobbyContainer: {
    padding: 20,
    gap: 20,
  },
  playersSection: {
    backgroundColor: '#fff',
    padding: 15,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  playerCount: {
    fontSize: 16,
    color: '#666',
    marginBottom: 15,
  },
  playerItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  connectedText: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
  },
  definitionEntryContainer: {
    flex: 1,
    padding: 20,
    justifyContent: 'space-between',
  },
  mainContent: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 20,
  },
  categoryBadge: {
    backgroundColor: '#1E4B8F',
    paddingHorizontal: 15,
    paddingVertical: 8,
    borderRadius: 20,
  },
  categoryText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  wordToDefine: {
    fontSize: 48,
    fontWeight: 'bold',
    color: '#E63946',
    textAlign: 'center',
  },
  definitionInput: {
    width: '100%',
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    fontSize: 18,
    minHeight: 120,
    textAlignVertical: 'top',
    shadowColor: '#1E4B8F',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  submitButton: {
    backgroundColor: '#1E4B8F',
    paddingHorizontal: 30,
    paddingVertical: 15,
    borderRadius: 25,
    marginTop: 20,
    alignItems: 'center',
  },
  submitButtonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
  },
  definitionsList: {
    flex: 1,
  },
  votingTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1E4B8F',
    textAlign: 'center',
    marginBottom: 20,
  },
  resultsContainer: {
    flex: 1,
    padding: 20,
  },
  resultsTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1E4B8F',
    textAlign: 'center',
    marginBottom: 20,
  },
  authorText: {
    fontSize: 14,
    color: '#666',
    fontStyle: 'italic',
  },
  votesText: {
    fontSize: 14,
    color: '#E63946',
    fontWeight: 'bold',
    marginTop: 5,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    width: '90%',
    maxWidth: 500,
    maxHeight: '80%',
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1E4B8F',
  },
  closeButton: {
    padding: 8,
  },
  closeButtonText: {
    fontSize: 24,
    color: '#666',
  },
  divider: {
    height: 1,
    backgroundColor: '#ddd',
    marginVertical: 20,
  },
  selectedDefinition: {
    backgroundColor: '#E6F3FF',
    borderColor: '#1E4B8F',
    borderWidth: 2,
  },
  loadingText: {
    fontSize: 18,
    color: '#666',
    textAlign: 'center',
  },
  errorText: {
    fontSize: 18,
    color: '#E63946',
    textAlign: 'center',
    marginBottom: 20,
  },
  submissionStatus: {
    fontSize: 18,
    color: '#666',
    textAlign: 'center',
  },
  waitingHint: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    marginTop: 8,
    fontStyle: 'italic',
  },
  prominentHint: {
    fontSize: 16,
    color: '#1E4B8F',
    backgroundColor: '#E3F2FD',
    padding: 12,
    borderRadius: 8,
    marginVertical: 12,
    fontWeight: '500',
    fontStyle: 'normal',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  moveToVotingButton: {
    backgroundColor: '#1E4B8F',
    marginTop: 20,
  },
  nextRoundButton: {
    marginTop: 20,
    marginBottom: 30,
  },
  scoreboardContainer: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    marginTop: 30,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  scoreboardTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1E4B8F',
    textAlign: 'center',
    marginBottom: 15,
  },
  scoreboardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 15,
    borderRadius: 8,
    marginBottom: 8,
    backgroundColor: '#f8f9fa',
  },
  firstPlace: {
    backgroundColor: '#FFF9C4',
  },
  secondPlace: {
    backgroundColor: '#F5F5F5',
  },
  thirdPlace: {
    backgroundColor: '#FFE0B2',
  },
  playerRank: {
    fontSize: 18,
    fontWeight: 'bold',
    width: 40,
    color: '#1E4B8F',
  },
  scoreboardPlayerName: {
    flex: 1,
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
  },
  scoreboardScore: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#E63946',
    marginLeft: 10,
  },
  correctDefinitionContainer: {
    backgroundColor: '#fff',
    padding: 20,
    borderRadius: 12,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  correctDefinitionLabel: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#444',
    marginBottom: 10,
  },
  correctDefinitionText: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#E63946',
    textAlign: 'center',
  },
  correctDefinitionCard: {
    backgroundColor: '#e8f5e9',
    borderColor: '#4caf50',
    borderWidth: 2,
  },
  correctBadge: {
    backgroundColor: '#4caf50',
    padding: 10,
    borderRadius: 8,
    marginTop: 10,
  },
  correctBadgeText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  correctNote: {
    color: '#fff',
    fontSize: 14,
    textAlign: 'center',
    marginTop: 5,
    fontStyle: 'italic',
  },
  submissionSuccessContainer: {
    backgroundColor: '#e8f5e9',
    padding: 20,
    borderRadius: 12,
    alignItems: 'center',
    marginTop: 20,
  },
  submissionSuccessIcon: {
    fontSize: 48,
    color: '#4caf50',
    marginBottom: 10,
  },
  submissionSuccessTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1E4B8F',
    marginBottom: 10,
    textAlign: 'center',
  },
  submissionSuccessText: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    fontStyle: 'italic',
  },
  votingHint: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    marginTop: 8,
    fontStyle: 'italic',
  },
  waitingStateContainer: {
    backgroundColor: '#E3F2FD',
    padding: 20,
    borderRadius: 12,
    marginTop: 20,
    alignItems: 'center',
  },
  waitingStateTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1E4B8F',
    marginBottom: 10,
  },
  waitingStateCount: {
    fontSize: 16,
    color: '#666',
  },
  ownDefinition: {
    opacity: 0.5,
    backgroundColor: '#f5f5f5',
  },
  ownDefinitionLabel: {
    fontSize: 14,
    color: '#666',
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: 5,
  },
  submittingVoteLabel: {
    fontSize: 14,
    color: '#666',
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: 5,
  },
}); 